import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { boardNotices, boardPosts } from '@/mocks/searchData';
import { useAuth } from '@/hooks/useAuth';
import supabase from '@/lib/supabase';

type TabType = 'notices' | 'community';
type SortMode = 'latest' | 'likes' | 'views';

interface SupabasePost {
  id: number;
  category: string;
  title: string;
  author: string;
  date: string;
  time: string;
  views: number;
  comments: number;
  likes: number;
}

interface RawPost {
  id: number;
  category: string;
  title: string;
  author: string;
  created_at: string;
  views: number;
}

const ITEMS_PER_PAGE = 10;

function formatDateOnly(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return '';
  }
}

function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return formatDateOnly(dateStr);
  } catch {
    return '';
  }
}

export default function Board() {
  const [activeTab, setActiveTab] = useState<TabType>('community');
  const [sortMode, setSortMode] = useState<SortMode>('latest');
  const [noticePage, setNoticePage] = useState(1);
  const [postPage, setPostPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [postCategory, setPostCategory] = useState('all');

  const [supabasePosts, setSupabasePosts] = useState<SupabasePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const { user } = useAuth();
  const writeLink = user ? '/board/write' : `/login?redirect=${encodeURIComponent('/board/write')}`;

  const pageSize = ITEMS_PER_PAGE;

  const fetchSupabasePosts = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .eq('is_hidden', false)
        .order('created_at', { ascending: false })
        .limit(200);

      if (postsError) throw postsError;

      const [commentsRes, likesRes] = await Promise.all([
        supabase.from('comments').select('post_id'),
        supabase.from('post_likes').select('post_id'),
      ]);

      const commentCounts: Record<string, number> = {};
      (commentsRes.data || []).forEach((c: { post_id: string }) => {
        commentCounts[c.post_id] = (commentCounts[c.post_id] || 0) + 1;
      });

      const likeCounts: Record<string, number> = {};
      (likesRes.data || []).forEach((l: { post_id: number }) => {
        likeCounts[String(l.post_id)] = (likeCounts[String(l.post_id)] || 0) + 1;
      });

      const formatted: SupabasePost[] = (postsData || []).map((p: RawPost) => ({
        id: p.id,
        category: p.category || '자유',
        title: p.title,
        author: p.author || '익명',
        date: formatDateOnly(p.created_at),
        time: formatRelativeTime(p.created_at),
        views: p.views || 0,
        comments: commentCounts[String(p.id)] || 0,
        likes: likeCounts[String(p.id)] || 0,
      }));

      setSupabasePosts(formatted.length > 0 ? formatted : boardPosts);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setFetchError(message);
      setSupabasePosts(boardPosts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSupabasePosts();
  }, [fetchSupabasePosts]);

  const sortedPosts = useMemo(() => {
    const posts = [...supabasePosts];
    switch (sortMode) {
      case 'likes':
        return posts.sort((a, b) => b.likes - a.likes);
      case 'views':
        return posts.sort((a, b) => b.views - a.views);
      default:
        return posts;
    }
  }, [supabasePosts, sortMode]);

  const categories = useMemo(() => {
    const cats = new Set(supabasePosts.map((p) => p.category));
    return ['all', ...Array.from(cats)];
  }, [supabasePosts]);

  const filteredPosts = useMemo(() => {
    let posts = [...sortedPosts];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      posts = posts.filter(
        (p) => p.title.toLowerCase().includes(q) || p.author.toLowerCase().includes(q)
      );
    }
    if (postCategory !== 'all') {
      posts = posts.filter((p) => p.category === postCategory);
    }
    return posts;
  }, [sortedPosts, searchQuery, postCategory]);

  const filteredNotices = useMemo(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return boardNotices.filter((n) => n.title.toLowerCase().includes(q));
    }
    return boardNotices;
  }, [searchQuery]);

  const noticeTotalPages = Math.ceil(filteredNotices.length / pageSize);
  const postTotalPages = Math.ceil(filteredPosts.length / pageSize);

  const pagedNotices = filteredNotices.slice((noticePage - 1) * pageSize, noticePage * pageSize);
  const pagedPosts = filteredPosts.slice((postPage - 1) * pageSize, postPage * pageSize);

  const Pagination = ({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) => {
    if (totalPages <= 1) return null;
    const pages: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div className="flex items-center justify-center gap-1 mt-6">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${
              p === page ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="w-8 h-8 flex items-center justify-center rounded text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    );
  };

  const categoryBadgeClass = (category: string) => {
    if (category === '피해사례') return 'bg-red-50 text-red-600';
    if (category === '정보공유') return 'bg-green-50 text-green-600';
    if (category === '검증요청') return 'bg-yellow-50 text-yellow-600';
    if (category === '질문') return 'bg-blue-50 text-blue-600';
    return 'bg-secondary-100 text-secondary-600';
  };

  const sortOptions: { mode: SortMode; label: string; icon: string }[] = [
    { mode: 'latest', label: '최신순', icon: 'ri-time-line' },
    { mode: 'likes', label: '추천순', icon: 'ri-thumb-up-line' },
    { mode: 'views', label: '조회순', icon: 'ri-eye-line' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">커뮤니티</h1>
            <p className="text-sm text-foreground-400">
              공지사항과 최신 커뮤니티 소식을 한눈에 확인하세요
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-4 w-fit">
            <button
              onClick={() => { setActiveTab('community'); setPostPage(1); setSearchQuery(''); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'community' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              <i className="ri-chat-3-line mr-1.5" />
              커뮤니티
            </button>
            <button
              onClick={() => { setActiveTab('notices'); setNoticePage(1); setSearchQuery(''); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === 'notices' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              <i className="ri-megaphone-line mr-1.5" />
              공지사항
            </button>
          </div>

          {activeTab === 'community' && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs text-foreground-500 whitespace-nowrap">정렬:</span>
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.mode}
                    onClick={() => { setSortMode(opt.mode); setPostPage(1); }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      sortMode === opt.mode
                        ? 'bg-white text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    <i className={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
            <div className="flex-1 flex w-full sm:max-w-md">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPostPage(1); setNoticePage(1); }}
                placeholder="제목 또는 작성자 검색"
                className="flex-1 bg-white text-foreground-950 text-sm px-4 py-2.5 rounded-l-md border border-foreground-200 focus:outline-none focus:border-primary-500"
              />
              <button className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2.5 rounded-r-md transition-colors">
                <i className="ri-search-line" />
              </button>
            </div>

            {activeTab === 'community' && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-600 whitespace-nowrap">분류:</span>
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => { setPostCategory(c); setPostPage(1); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      postCategory === c
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {c === 'all' ? '전체' : c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {activeTab === 'community' && fetchError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-4 flex items-center gap-2">
              <i className="ri-error-warning-line text-yellow-600" />
              <p className="text-sm text-yellow-700">
                Supabase 연결에 실패했습니다. 목데이터를 표시합니다. 새로고침하거나{' '}
                <button onClick={fetchSupabasePosts} className="underline font-medium hover:text-yellow-800">
                  다시 시도
                </button>
              </p>
            </div>
          )}

          <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
            {activeTab === 'notices' ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-foreground-200 bg-background-100">
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-16 whitespace-nowrap">번호</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">제목</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-20 whitespace-nowrap">작성자</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-24 whitespace-nowrap">날짜</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-20 whitespace-nowrap">조회</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedNotices.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-16 text-center text-sm text-foreground-500">
                            검색 결과가 없습니다
                          </td>
                        </tr>
                      ) : (
                        pagedNotices.map((notice) => (
                          <tr
                            key={notice.id}
                            className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-center">
                              {notice.pinned ? (
                                <span className="inline-flex items-center justify-center w-6 h-6 bg-primary-100 rounded">
                                  <i className="ri-pushpin-line text-primary-500 text-xs" />
                                </span>
                              ) : (
                                <span className="text-sm text-foreground-500">{notice.id}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <Link to={`/board/${notice.id}`} className="text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors">
                                {notice.title}
                              </Link>
                              <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-600 font-medium">
                                {notice.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-600">{notice.author}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-500">{notice.date}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-500">{notice.views.toLocaleString()}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination page={noticePage} totalPages={noticeTotalPages} onPageChange={setNoticePage} />
              </>
            ) : loading ? (
              <div className="py-20 text-center">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-foreground-500">게시글을 불러오는 중...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-foreground-200 bg-background-100">
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-16 whitespace-nowrap">번호</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-20 whitespace-nowrap">분류</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">제목</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-24 whitespace-nowrap">작성자</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-24 whitespace-nowrap">날짜</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-16 whitespace-nowrap">조회</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-14 whitespace-nowrap">추천</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 w-14 whitespace-nowrap">댓글</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedPosts.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-16 text-center text-sm text-foreground-500">
                            검색 결과가 없습니다
                          </td>
                        </tr>
                      ) : (
                        pagedPosts.map((post) => (
                          <tr
                            key={post.id}
                            className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-500">{post.id}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${categoryBadgeClass(post.category)}`}>
                                {post.category}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Link to={`/board/${post.id}`} className="text-sm font-medium text-foreground-950 hover:text-primary-500 transition-colors">
                                {post.title}
                              </Link>
                              {post.comments > 0 && (
                                <span className="ml-1.5 text-xs text-primary-500 font-medium">
                                  [{post.comments}]
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-600">{post.author}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-500 whitespace-nowrap">{post.time || post.date}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-500">{post.views.toLocaleString()}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-sm font-medium ${post.likes > 0 ? 'text-primary-500' : 'text-foreground-400'}`}>
                                {post.likes > 0 ? post.likes : '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-sm font-medium ${post.comments > 0 ? 'text-primary-500' : 'text-foreground-400'}`}>
                                {post.comments > 0 ? post.comments : '-'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination page={postPage} totalPages={postTotalPages} onPageChange={setPostPage} />
              </>
            )}
          </div>

          <div className="mt-6 text-right">
            <Link
              to={writeLink}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap"
            >
              <i className="ri-edit-line" />
              글쓰기
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}